import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, Performance, Session, Task, User } from "../api";
import { dayCount, isoDate } from "../lib/format";
import { DataTable, Empty, LevelSegments, Loading, PageHead } from "../components/ui";

type Period = { key: string; label: string; from: string; to: string };

export function periods(today = new Date(), locale = "ko"): Period[] {
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const half = month > 6 ? 2 : 1;
  const quarter = Math.floor((month - 1) / 3) + 1;
  const pad = (value: number) => String(value).padStart(2, "0");
  const quarterStart = (quarter - 1) * 3 + 1;
  const sixWeeksAgo = new Date(today.getTime() - 42 * 86400000);
  return [
    { key: "half", label: locale.startsWith("en") ? `${year} H${half}` : `${year} ${half === 1 ? "상반기" : "하반기"}`, from: `${year}-${half === 1 ? "01-01" : "07-01"}`, to: today.toISOString().slice(0, 10) },
    { key: "quarter", label: locale.startsWith("en") ? `${year} Q${quarter}` : `${year} ${quarter}분기`, from: `${year}-${pad(quarterStart)}-01`, to: today.toISOString().slice(0, 10) },
    { key: "weeks", label: locale.startsWith("en") ? "Last 6 weeks" : "최근 6주", from: sixWeeksAgo.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) },
  ];
}

export function PerformancePage({ session, users, tasks }: { session: Session; users: User[]; tasks: Task[] }) {
  const { t, i18n } = useTranslation();
  const options = useMemo(() => periods(new Date(), i18n.resolvedLanguage || i18n.language), [i18n.language, i18n.resolvedLanguage]);
  const [periodKey, setPeriodKey] = useState(options[0].key);
  const [data, setData] = useState<Performance | null>(null);
  const [error, setError] = useState("");
  const period = options.find((item) => item.key === periodKey) || options[0];
  const me = users.find((user) => user.id === session.userId);

  useEffect(() => {
    setData(null); setError("");
    void api.performance(session.userId, period.from, period.to).then(setData)
      .catch((reason) => setError(reason instanceof Error ? reason.message : t("performance.loadError")));
  }, [session.userId, period.from, period.to]);

  async function downloadCsv() {
    try {
      const blob = await api.downloadPerformance(session.userId, period.from, period.to);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `performance-${session.userId}-${period.from}-${period.to}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : t("performance.exportError")); }
  }

  const mine = tasks.filter((task) => task.assigneeIds.includes(session.userId));
  const completed = mine.filter((task) => task.status === "COMPLETED" && isoDate(task.completedAt) >= period.from && isoDate(task.completedAt) <= period.to);

  return <>
    <PageHead title={t("performance.title")} meta={`${me?.displayName || t("performance.me")} · ${me?.jobTitle || t("performance.member")} · ${period.from} — ${period.to}`} controls={<>
      <select aria-label={t("performance.period")} aria-describedby="performance-period-basis" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)}>{options.map((item) => <option value={item.key} key={item.key}>{item.label}</option>)}</select>
      <button type="button" className="secondary" onClick={() => void downloadCsv()}><Download size={14} /> CSV</button>
    </>} />
    <p id="performance-period-basis" className="muted">{t("performance.periodBasis")}</p>
    {error && <div className="global-error">{error}</div>}
    {!data ? <Loading layout="summary" label={t("performance.loading")} /> : <>
      <div className="stat-row">
        <div className="stat"><div className="mono-label">{t("performance.taskCount")}</div><strong>{data.taskCount}</strong><small>{t("performance.taskCountHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("performance.quality")}</div><strong>{data.averageQualityScore === null || data.averageQualityScore === undefined ? "-" : `${data.averageQualityScore.toFixed(1)} / 5`}</strong><small>{t("performance.qualityHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("performance.onTime")}</div><strong>{data.onTimeRate === null || data.onTimeRate === undefined ? "-" : `${Math.round(data.onTimeRate)}%`}</strong><small>{t("performance.onTimeHelp")}</small></div>
        <div className="stat"><div className="mono-label">{t("performance.difficultyMix")}</div><strong className="mono">{["HIGH", "MEDIUM", "LOW"].map((key) => data.difficultyCounts[key] || 0).join(" / ")}</strong><small>{t("performance.difficultyMixHelp")}</small></div>
      </div>

      <section className="section-card">
        <header><div><span className="mono-label">COMPLETED</span><h2>{t("performance.completed")}</h2></div><span className="mono-label">{t("performance.durationHelp")}</span></header>
        <DataTable cols="minmax(240px, 2fr) 90px 80px 90px 110px">
          <div className="data-row data-head"><span>{t("performance.task")}</span><span>{t("performance.difficulty")}</span><span>{t("performance.round")}</span><span>{t("performance.duration")}</span><span>{t("performance.completedOn")}</span></div>
          {completed.map((task) => {
            const days = task.startsOn && task.completedAt ? Math.max(0, -(dayCount(isoDate(task.startsOn), new Date(task.completedAt)) ?? 0)) : null;
            return <div className="data-row" key={task.id}>
              <span className="title-cell"><b>{task.internalTitle}</b>{!task.countsForEvaluation && <small>{t("performance.excluded")}</small>}</span>
              <span><LevelSegments difficulty={task.difficulty} /></span>
              <span className="mono">{t("performance.roundCount", { count: task.rounds.length })}</span>
              <span className="mono">{days === null ? "-" : t("performance.days", { count: days })}</span>
              <span className="mono">{isoDate(task.completedAt) || "-"}</span>
            </div>;
          })}
        </DataTable>
        {completed.length === 0 && <Empty title={t("performance.empty")} copy={t("performance.emptyHelp")} />}
      </section>
    </>}
  </>;
}
